import { useEffect, useMemo, useRef } from "react";
import maplibregl from "maplibre-gl";

import { basemap3dEnabled } from "../lib/basemap3d.js";
import { pickName, tr } from "../lib/i18n.jsx";
import { lowPower } from "../lib/lowPower.js";
import { addVenueLayers, setFloor, setSelectedFeature } from "./lib/mapLayers.js";
import { addModelLayer } from "./lib/modelLayer.js";
import { createPoiMarkers, extrusionsFromFC } from "./lib/poiMarkers.js";
import { addYouAreHere } from "./lib/youAreHere.js";

/** How far (px) around a marker a click still counts as hitting it. A kiosk
 *  finger covers ~40 px; a mouse pointer is exact. Synthesised clicks carry
 *  pointerType in Chromium; elsewhere fall back to the device's main pointer. */
function touchTolerance(e) {
  const type = e.originalEvent?.pointerType;
  const coarse = type
    ? type !== "mouse"
    : typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
  return coarse ? 12 : 2;
}

/** Owns the MapLibre instance. React drives it imperatively through effects —
 *  the map is never re-created after mount (venue/features are load-once). */
export default function MapCanvas({
  venue,
  featuresFC,
  pois,
  lang,
  activeFloorId,
  selectedPoi,
  kiosk,
  accent,
  camera,
  onSelectPoi,
  onMapReady,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const readyRef = useRef(false);
  const prevFeatureRef = useRef(null);
  const kioskMarkerRef = useRef(null);
  const modelsRef = useRef(null);
  const markersRef = useRef(null);

  // Latest-value refs so the one-time load handler and layer callbacks never
  // see stale props.
  // Category icon + colour drive the marker discs of logo-less POIs.
  const categoriesById = useMemo(
    () => Object.fromEntries((venue?.categories ?? []).map((c) => [c.id, c])),
    [venue],
  );

  const poisRef = useRef(pois);
  poisRef.current = pois;
  const categoriesRef = useRef(categoriesById);
  categoriesRef.current = categoriesById;
  const langRef = useRef(lang);
  langRef.current = lang;
  const floorRef = useRef(activeFloorId);
  floorRef.current = activeFloorId;
  const onSelectRef = useRef(onSelectPoi);
  onSelectRef.current = onSelectPoi;
  const onReadyRef = useRef(onMapReady);
  onReadyRef.current = onMapReady;
  // The opening frame is decided by the viewer (ViewerPage.homeCamera), which
  // is the only place that knows whether this session is running on a totem.
  // Read through a ref so the map is still created exactly once.
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  useEffect(() => {
    if (!venue || !featuresFC || mapRef.current) return;
    const s = venue.settings ?? {};
    const cam = cameraRef.current ?? {};
    const lp = lowPower().on;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: s.basemap_style_url,
      center: cam.center ?? s.center ?? [0, 0],
      zoom: cam.zoom ?? s.zoom ?? 17,
      bearing: cam.bearing ?? s.bearing ?? 0,
      pitch: cam.pitch ?? s.pitch ?? 50,
      // Kiosk fence: visitors can't pan/zoom away from the venue. [[w,s],[e,n]]
      maxBounds: s.max_bounds ?? undefined,
      minZoom: s.min_zoom ?? 15,
      attributionControl: { compact: true },
      // Keeps the WebGL buffer readable: headless screenshots (CI/visual
      // checks) and any future "save map as image" both need it. Low-power
      // mode drops it: on a mobile GPU it costs a full-frame copy per frame.
      canvasContextAttributes: { preserveDrawingBuffer: !lp },
      // Low-power mode (item 0315, lib/lowPower.js). Absent keys keep
      // MapLibre's defaults, so a capable device renders exactly as before.
      // NOT fadeDuration: 0 -- measured, it made panning MORE expensive on a
      // throttled device (main thread 0.92 s/s vs 0.63), because MapLibre
      // forces a full symbol placement on every frame when fade is 0
      // (style.ts _updatePlacement).
      ...(lp ? { pixelRatio: Math.min(window.devicePixelRatio || 1, 1), maxTileCacheSize: 24 } : {}),
    });
    mapRef.current = map;
    if (import.meta.env.DEV) window.__map = map;

    map.on("load", () => {
      // Collapse the attribution to its ⓘ pill (it starts expanded). The
      // credits stay one tap away — required by the OSM/OpenFreeMap licenses.
      const attrib = map.getContainer().querySelector(".maplibregl-ctrl-attrib");
      attrib?.classList.remove("maplibregl-compact-show");
      attrib?.removeAttribute("open");

      addVenueLayers(map, featuresFC, floorRef.current, { basemap3d: basemap3dEnabled(s) });

      // POI markers: HTML, standing on the roof under each POI (item 0371).
      const markers = createPoiMarkers(map);
      markersRef.current = markers;
      markers.setFloor(floorRef.current);
      markers.setPois(poisRef.current, {
        name: (p) => pickName(p.name, langRef.current),
        categoriesById: categoriesRef.current,
        extrusions: extrusionsFromFC(featuresFC),
      });

      // 3D models (three.js) load asynchronously and only when the venue has
      // any — a map with none never pays the cost.
      // venueSlug: model versions come from this venue's own list now that
      // the global catalogue no longer publishes every tenant's uploads.
      // Low-power mode skips them altogether: they are decoration, and three.js
      // plus a custom layer that draws on every map frame is the single most
      // expensive optional thing on the page.
      (lp ? Promise.resolve(null) : addModelLayer(map, featuresFC, floorRef.current, { venueSlug: venue.slug }))
        .then((controller) => {
          if (!controller) return;
          if (mapRef.current !== map) return controller.destroy(); // unmounted
          modelsRef.current = controller;
          controller.setFloor(floorRef.current);
        })
        .catch((e) => console.warn("model layer failed", e));

      readyRef.current = true;

      // ONE click handler, in priority order (item 0371). There used to be two
      // layer-bound handlers, `poi-symbols` then `units`; MapLibre fires every
      // matching one, so a tap on a marker drawn over another shop's roof
      // selected the marker's POI and was then overwritten by the roof's.
      // A marker hit now ends the click; the roof is asked only on a miss.
      map.on("click", (e) => {
        const slug = markers.pick(e, touchTolerance(e));
        if (slug) {
          onSelectRef.current?.(slug);
          return;
        }
        // Nearest-first: MapLibre sorts extrusion hits by depth, so [0] is
        // the roof or wall actually under the pointer.
        const f = map.queryRenderedFeatures(e.point, { layers: ["units"] })[0];
        if (!f) return;
        const poi = poisRef.current.find((p) => p.feature_id === f.id);
        if (poi) onSelectRef.current?.(poi.slug);
      });

      onReadyRef.current?.(map);
    });

    return () => {
      markersRef.current?.destroy();
      markersRef.current = null;
      modelsRef.current = null; // map.remove() tears the custom layer down
      map.remove();
      mapRef.current = null;
      readyRef.current = false;
      kioskMarkerRef.current = null;
    };
  }, [venue, featuresFC]); // eslint-disable-line react-hooks/exhaustive-deps

  // Language / POI-list changes: rebuild the markers.
  useEffect(() => {
    if (!readyRef.current) return;
    markersRef.current?.setPois(pois, {
      name: (p) => pickName(p.name, lang),
      categoriesById,
    });
  }, [pois, lang, categoriesById]);

  // Floor switching.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    setFloor(map, activeFloorId);
    modelsRef.current?.setFloor(activeFloorId);
    markersRef.current?.setFloor(activeFloorId);
  }, [activeFloorId]);

  // Kiosk marker (re-created on language change for the label, and on accent
  // change because the marker follows the venue's route colour -- item 0106).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !kiosk) return;
    const place = () => {
      kioskMarkerRef.current?.remove();
      kioskMarkerRef.current = addYouAreHere(map, kiosk, tr("you_are_here"), accent);
    };
    if (readyRef.current) place();
    else map.once("load", place);
  }, [kiosk, lang, accent]);

  // Selection: highlight the unit polygon + ease the camera to the POI.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const featureId = selectedPoi?.feature_id ?? null;
    setSelectedFeature(map, featureId, prevFeatureRef.current);
    prevFeatureRef.current = featureId;
    markersRef.current?.setSelected(selectedPoi?.slug ?? null);
    if (selectedPoi) {
      map.easeTo({
        center: [selectedPoi.lng, selectedPoi.lat],
        zoom: Math.max(map.getZoom(), 17.8),
        duration: 700,
      });
    }
  }, [selectedPoi]);

  // Inline style, not Tailwind classes: maplibre-gl.css sets `.maplibregl-map
  // { position: relative }` unlayered, which beats Tailwind's layered
  // `.absolute` in the cascade and collapses the map to 0 height.
  return <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />;
}
